import type { ApiFailureDetails } from '@flashka/shared/api';
import { StorageReadTimeoutError } from '@flashka/shared/storage/fetch-stored';

/**
 * One retry rule for react-query, mirroring the rule the DO layer already has.
 *
 * `party/framework/do-rpc.ts` decides what a failed RPC is worth repeating:
 * 408, 429 and 5xx, nothing else. The browser had no such rule. `queryClient`
 * (`src/lib/query-client.ts`) sets no `defaultOptions`, so every query that
 * throws on a non-ok response inherited react-query's stock `retry: 3` and
 * repeated terminal answers three more times.
 *
 * That is measured, not theoretical. A `groupMemberships` row pointing at a
 * DELETED study group makes the sidebar's per-group queries 404 on every
 * mount — and the sidebar mounts on every route, so every navigation pays it.
 * Each of those 404s was then retried three times, and each attempt is a REST
 * call that the Hono bridge (`server/routes/study-groups-do-bridge.ts`) turns
 * into a `POST /parties/study-group-do/<id>/internal/...` and a Durable Object
 * wake-up. One local e2e suite run logged ~10.9k such 404s against five dead
 * groups.
 *
 * A 404 does not become a 200 by being asked again. Retry what can change;
 * stop on what cannot.
 *
 * NOT to be confused with `src/lib/retry-fetch.ts`, which is a different layer
 * and deliberately left alone: that one wraps a single `fetch` and RETURNS the
 * final failing response, so a caller can read its status. This one is the
 * react-query `retry` predicate — `(failureCount, error) => boolean` — and the
 * two cannot share an implementation. They do state nearly the same policy;
 * `retryFetch` differs in not retrying 408. Neither is wrong for its layer, but
 * a third opinion about what is transient should not be added without merging
 * these two first. The storage-download rule below lives here for that reason.
 */

/**
 * What our API said about a failed answer beyond its status: the refusal's
 * stable `reason` code, and the two ids that find the server's side of it.
 * Read by `apiFailureDetails` (`@flashka/shared/api`, shared with mobile).
 */
export type HttpFailureDetails = ApiFailureDetails;

/**
 * Thrown by a `queryFn` in place of a bare `Error` so the retry rule can see
 * the status the server actually sent. Without it the status survives only as
 * text inside `error.message`, which is not something a policy should parse.
 * The optional details ride along to the Sentry event
 * (`httpErrorReportContext`).
 */
export class HttpStatusError extends Error {
  readonly status: number;
  readonly details: HttpFailureDetails;

  constructor(status: number, message: string, details: HttpFailureDetails = {}) {
    super(message);
    this.name = 'HttpStatusError';
    this.status = status;
    this.details = details;
  }
}

/**
 * The same predicate as `isRetryable` in `party/framework/do-rpc.ts`. Kept as
 * its own copy rather than imported: that module is worker code and pulls the
 * DO bindings in with it. The two are four lines and one comment apart — if
 * one changes, change both.
 */
function isRetryableStatus(status: number): boolean {
  if (status === 408 || status === 429) return true;
  return status >= 500 && status < 600;
}

/**
 * A react-query `retry` for any query whose `queryFn` throws
 * `HttpStatusError`. A terminal status stops immediately; everything else
 * keeps the stock budget (`maxRetries` retries after the first failure, so
 * `retry: 3`'s four attempts by default).
 *
 * Anything that is NOT an `HttpStatusError` — a dropped connection, an abort,
 * a parse failure — also keeps the stock budget, because those are exactly the
 * transient failures retrying exists for.
 */
export function retryUnlessTerminal(maxRetries = 3) {
  return (failureCount: number, error: unknown): boolean => {
    if (error instanceof HttpStatusError && !isRetryableStatus(error.status)) {
      return false;
    }
    return failureCount <= maxRetries;
  };
}

/**
 * A react-query `retry` for a storage download: react-query's stock three
 * retries, except after a storage timeout. `fetchStored` has already asked
 * twice by then, and three more rounds would keep the viewer spinning for
 * minutes before it shows the error.
 */
export function retryUnlessStorageTimedOut(
  failureCount: number,
  error: unknown,
): boolean {
  if (error instanceof StorageReadTimeoutError) return false;
  return failureCount < 3;
}
